import type { FormaPago, Job, JobStatus, JobCompletionChecklist } from '@conversia/shared-types';
import type { Paginated } from '@/lib/types';
import { api } from './client';

/** Endpoints de trabajos/servicios (ADMIN). Prefijo: /api/jobs */

/** Job con las relaciones que devuelve la API (contacto + técnico resumidos). */
export interface JobWithRelations extends Job {
  contact?: { id: string; name: string | null; pushName: string | null; phone: string | null } | null;
  employee?: { id: string; nombre: string; fotoUrl: string | null; rol: string } | null;
}

export interface ListJobsParams {
  estado?: JobStatus;
  employeeId?: string;
  contactId?: string;
  /** Rango de citas (ISO absoluto); un trabajo sin cita queda fuera del rango. */
  from?: string;
  to?: string;
  skip?: number;
  take?: number;
}

export const listJobs = (params: ListJobsParams = {}) =>
  api.get<Paginated<JobWithRelations>>('/api/jobs', { query: params });

export const getJob = (id: string) => api.get<JobWithRelations>(`/api/jobs/${id}`);

export const createJob = (input: {
  contactId: string;
  conversationId?: string | null;
  employeeId?: string | null;
  descripcion: string;
  direccion?: string | null;
  zona?: string | null;
  scheduledAt?: string | null;
  esGarantia?: boolean;
  targetLat?: number | null;
  targetLng?: number | null;
  geofenceRadiusM?: number | null;
  mapsUrl?: string | null;
}) => api.post<JobWithRelations>('/api/jobs', input);

/** Trabajos con cita en [from, to] (ISO) para la vista Despachos. */
export const listDespachos = (from: string, to: string) =>
  api.get<JobWithRelations[]>('/api/jobs/despachos', { query: { from, to } });

/** Corrección manual de la ficha de despacho de un trabajo. */
export const updateDespacho = (
  id: string,
  patch: {
    vendedor?: string | null;
    formaPago?: FormaPago | null;
    comprobanteFiscal?: 'si' | 'no' | null;
    rnc?: string | null;
    compania?: string | null;
    mapsUrl?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}/despacho`, patch);

/**
 * Edición completa post-creación: identidad/agenda del trabajo, sin tocar
 * estado ni ponches. En un trabajo cerrado el server solo acepta el registro
 * administrativo (descripción/dirección/zona/notas).
 */
export const updateJob = (
  id: string,
  patch: {
    descripcion?: string;
    direccion?: string | null;
    zona?: string | null;
    notas?: string | null;
    scheduledAt?: string | null;
    employeeId?: string | null;
    targetLat?: number | null;
    targetLng?: number | null;
    geofenceRadiusM?: number | null;
    mapsUrl?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}`, patch);

export const assignEmployee = (id: string, employeeId: string | null) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/assign`, { employeeId });

/** Borra un trabajo. `force` confirma borrar un COMPLETADO con encuesta (409 sin él). */
export const deleteJob = (id: string, force = false) =>
  api.del<{ deleted: boolean }>(`/api/jobs/${id}`, force ? { query: { force: 'true' } } : undefined);

export const setWarranty = (id: string, esGarantia: boolean) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/warranty`, { esGarantia });

export const setJobTarget = (
  id: string,
  target: { targetLat: number | null; targetLng: number | null; geofenceRadiusM: number | null },
) => api.post<JobWithRelations>(`/api/jobs/${id}/target`, target);

export const changeStatus = (
  id: string,
  input: { estado: JobStatus; resultado?: string | null; motivo?: string | null },
) => api.post<JobWithRelations>(`/api/jobs/${id}/status`, input);

/**
 * Validación de supervisión (portado de silvai-chat): cierra en firme un trabajo
 * que el técnico dejó "Por validar". Las garantías van sin monto, pago ni vendedor.
 */
export const validarCierre = (
  id: string,
  input: {
    montoCierre?: number | null;
    formaPago?: string | null;
    vendedorUserId?: string | null;
    rnc?: string | null;
    comprobanteFiscal?: string | null;
    /** Corrección de la tipificación de una garantía; si no van, se confirma la del técnico. */
    nivelResolucion?: string | null;
    causaRaiz?: string | null;
  },
) => api.post<JobWithRelations>(`/api/jobs/${id}/validar`, input);

/** Marca que el cliente pidió comprobante fiscal: validar exigirá RNC y número. */
export const setComprobanteFiscal = (id: string, requiereComprobanteFiscal: boolean) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/comprobante-fiscal`, { requiereComprobanteFiscal });

/** Un vendedor posible: miembro activo del negocio activo. */
export interface Vendedor {
  id: string;
  name: string | null;
  email: string;
}

/** Vendedores del negocio activo (no todos los usuarios de la organización). */
export const listVendedores = () => api.get<Vendedor[]>('/api/jobs/vendedores');

/** Cancela un trabajo abierto (Fase 2): decisión del admin, motivo obligatorio. */
export const cancelJob = (id: string, motivo: string) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/cancel`, { motivo });

/** Edita el reporte de cierre de un trabajo cerrado (Fase 2, solo ADMIN). */
export const updateCierre = (
  id: string,
  patch: {
    resultado?: string | null;
    motivo?: string | null;
    checklist?: JobCompletionChecklist | null;
    registroProducto?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}/cierre`, patch);

/** Una entrada del historial de la ficha (Fase 1): quién, cuándo y qué cambió. */
export interface JobHistoryItem {
  id: string;
  at: string;
  action: string;
  userName: string;
  changes: Record<string, { antes: unknown; despues: unknown }> | null;
  fields: string[];
}

export const getJobHistory = (id: string) =>
  api.get<JobHistoryItem[]>(`/api/jobs/${id}/history`);
